import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ClientHeader from "../../components/Client/ClientHeader";
import Footer from "../../components/Client/Footer";
import AddressFormModal from "../../components/Client/AddressFormModal";
import { useAuth } from "../../context/AuthContext";
import { fetchAddresses, removeAddress, setDefaultAddress } from "../../api/addressService";
import "../../components/Client/clientTheme.css";
import "./Addresses.css";

function PlusIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function PinIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round">
      <path d="M12 22s7-7.05 7-12a7 7 0 1 0-14 0c0 4.95 7 12 7 12z" />
      <circle cx="12" cy="10" r="2.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

export default function Addresses() {
  const { t } = useTranslation(["addresses", "common"]);
  const { isAuthenticated, openLoginModal } = useAuth();
  const [addresses, setAddresses] = useState(null);
  const [error, setError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    if (!isAuthenticated) return;
    setError(null);
    fetchAddresses()
      .then(setAddresses)
      .catch(() => setError(t("loadErrorMessage")));
  }, [isAuthenticated, t]);

  useEffect(() => {
    load();
  }, [load]);

  function openAddForm() {
    setEditingAddress(null);
    setFormOpen(true);
  }

  function openEditForm(address) {
    setEditingAddress(address);
    setFormOpen(true);
  }

  function handleSaved(saved) {
    setFormOpen(false);
    setAddresses((prev) => {
      if (!prev) return [saved];
      const exists = prev.some((a) => a.addressId === saved.addressId);
      const next = exists ? prev.map((a) => (a.addressId === saved.addressId ? saved : a)) : [saved, ...prev];
      return saved.isDefault ? next.map((a) => (a.addressId === saved.addressId ? a : { ...a, isDefault: false })) : next;
    });
  }

  async function handleDelete(addressId) {
    if (!window.confirm(t("removeConfirmMessage"))) return;
    setBusyId(addressId);
    try {
      await removeAddress(addressId);
      setAddresses((prev) => prev.filter((a) => a.addressId !== addressId));
    } catch {
      setError(t("removeErrorMessage"));
    } finally {
      setBusyId(null);
    }
  }

  async function handleSetDefault(addressId) {
    setBusyId(addressId);
    try {
      await setDefaultAddress(addressId);
      setAddresses((prev) => prev.map((a) => ({ ...a, isDefault: a.addressId === addressId })));
    } catch {
      setError(t("setDefaultErrorMessage"));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="client-addresses-page">
      <ClientHeader />

      <div className="client-addresses-content">
        <div className="client-addresses-header-row">
          <h1 className="client-addresses-title">{t("pageTitle")}</h1>
          {isAuthenticated && (
            <button type="button" className="client-addresses-add-button" onClick={openAddForm}>
              <PlusIcon />
              {t("addAddressButton")}
            </button>
          )}
        </div>

        {!isAuthenticated ? (
          <div className="client-addresses-empty">
            <p>{t("loginPrompt")}</p>
            <button type="button" className="client-addresses-login-button" onClick={openLoginModal}>
              {t("common:logIn")}
            </button>
          </div>
        ) : error ? (
          <div className="client-addresses-empty">
            <p>{error}</p>
            <button type="button" className="client-addresses-login-button" onClick={load}>
              {t("common:tryAgain")}
            </button>
          </div>
        ) : addresses === null ? (
          <div className="client-addresses-empty">
            <p>{t("loadingMessage")}</p>
          </div>
        ) : addresses.length === 0 ? (
          <div className="client-addresses-empty">
            <PinIcon />
            <p>{t("emptyMessage")}</p>
            <button type="button" className="client-addresses-login-button" onClick={openAddForm}>
              {t("addFirstAddressButton")}
            </button>
          </div>
        ) : (
          <div className="client-addresses-list">
            {addresses.map((address) => (
              <div className="client-address-card" key={address.addressId}>
                <div className="client-address-card-icon">
                  <PinIcon />
                </div>

                <div className="client-address-card-body">
                  <div className="client-address-card-top-row">
                    <span className="client-address-card-title">{address.title || t("addressFallbackTitle")}</span>
                    {address.isDefault && <span className="client-address-default-badge">{t("defaultBadge")}</span>}
                  </div>
                  <p className="client-address-card-line">
                    {address.street}
                    {address.buildingNumber ? ` ${address.buildingNumber}` : ""}
                    {address.apartmentNumber ? t("apartmentSuffix", { number: address.apartmentNumber }) : ""}
                  </p>
                  <p className="client-address-card-line secondary">
                    {address.cityNameEnglish || address.city}
                  </p>
                  {address.additionalDetails && (
                    <p className="client-address-card-line secondary">{address.additionalDetails}</p>
                  )}

                  <div className="client-address-card-actions">
                    {!address.isDefault && (
                      <button
                        type="button"
                        className="client-address-action-link"
                        onClick={() => handleSetDefault(address.addressId)}
                        disabled={busyId === address.addressId}
                      >
                        {t("setAsDefaultButton")}
                      </button>
                    )}
                    <button
                      type="button"
                      className="client-address-action-link"
                      onClick={() => openEditForm(address)}
                      disabled={busyId === address.addressId}
                    >
                      {t("common:edit")}
                    </button>
                    <button
                      type="button"
                      className="client-address-action-link danger"
                      onClick={() => handleDelete(address.addressId)}
                      disabled={busyId === address.addressId}
                    >
                      {t("common:delete")}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Footer />

      {formOpen && (
        <AddressFormModal address={editingAddress} onClose={() => setFormOpen(false)} onSaved={handleSaved} />
      )}
    </div>
  );
}
